// Imports
import { useEffect, useState } from "react";
import { getGroupNames, getColumnValues, getTopWords, getTopCollocations, getEmbedCols, getTextCols } from "../../../../api"
import { Paper, Button, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography, Box } from "@mui/material";
import { metricNames, metricSettings, posOptionalMetrics, embeddingMetrics, posOptions, metricTypes, clusteringMetrics } from "./metrics.js";
import { FormattedMultiTextField, FormattedMultiSelectField, FormattedTextField } from "../../../common";
import "../../../../styles/List.css";
import { useNavigate } from "react-router-dom";

const allMetricOptions = Object.keys(metricNames).map(x => {
   return {
       "value": x,
       "label": metricNames[x]
   }
});

export const GraphSettings = ( props ) => {
   // UseState definitions
   const [disabled, setDisabled] = useState(true);
   const [disabledMessage, setDisabledMessage] = useState("");
   const [searchTerms, setSearchTerms] = useState([]);
   const [checkGroupOptions, setCheckGroupOptions] = useState(false);
   const [groupOptions, setGroupOptions] = useState([]);
   const [groupList, setGroupList] = useState([]);
   const [networkValid, setNetworkValid] = useState(false);
   const [toCol, setToCol] = useState("");
   const [fromCol, setFromCol] = useState("");
   const [refreshGroupOptions, setRefreshGroupOptions] = useState(true);
   const [metric, setMetric] = useState("counts");
   const [selectToggle, setSelectToggle] = useState(false);
   const [metricOptions, setMetricOptions] = useState([ ...allMetricOptions ]);
   const [posValid, setPosValid] = useState(false);
   const [posList, setPosList] = useState([]);
   const [topn, setTopn] = useState("5");
   const [numClusters, setNumClusters] = useState("5");
   const [savedSettings, setSavedSettings] = useState(undefined);
   const [firstUpdate, setFirstUpdate] = useState(true);
   const [embedCols, setEmbedCols] = useState([]);
   const [lastMetric, setLastMetric] = useState("counts");
   const [dataSource, setDataSource] = useState("tokens");


   // For multiple text mined columns
   const [selectedTextCols, setSelectedTextCols] = useState([]);
   const [textColOptions, setTextColOptions] = useState([]);
   const [selectedGroupCols, setSelectedGroupCols] = useState([]);


   const navigate = useNavigate();


   // UseEffect: Updates graph settings from local storage and group names from api
   useEffect(() => {
       const settings = JSON.parse(localStorage.getItem("graph-settings"));
       setSavedSettings(settings);
       if(settings && settings.table_name === props.dataset.dataset.table_name){
           setCheckGroupOptions(false);
           setMetric(settings.metric);
           setLastMetric(settings.metric);
           setTopn(String(settings.topn));
           setNumClusters(String(settings.num_clusters));
           setToCol(settings.to_col);
           setFromCol(settings.from_col);
           if (settings.data_source) {
               setDataSource(settings.data_source);
           }


           let searchList = []
           settings.group_list.forEach(x => {
               let object = { label: x, value: x };
               searchList.push(object)
           })
           setGroupList(searchList);


           let wordList = []
           settings.word_list.forEach(x => {
               let object = { label: x, value: x };
               wordList.push(object)
           })
           setSearchTerms(wordList);


           let pos_list = []
           settings.pos_list.forEach(x => {
               const record = posOptions.filter(y => y.value === x);
               if (record.length > 0) {
                   pos_list.push(record[0]);
               }
           })
           setPosList(pos_list);


           if (settings.text_columns) {
               const textColList = settings.text_columns.map(col => ({
                   label: col.replace(/_/g, ' '),
                   value: col
               }));
               setSelectedTextCols(textColList);
           }


           // Handle saved group columns (backward compatibility)
           if (settings.group_names) {
               const groupColList = settings.group_names.map(col => ({
                   label: col.replace(/_/g, ' '),
                   value: col
               }));
               setSelectedGroupCols(groupColList);
           } else if (settings.group_name) {
               // Backward compatibility: convert single group_name to array
               setSelectedGroupCols([{
                   label: settings.group_name.replace(/_/g, ' '),
                   value: settings.group_name
               }]);
           }
       }


       updateGroupNames();


       if (!props.dataset.dataset.embeddings || !props.dataset.dataset.embeddings_done) {
           setMetricOptions([ ...metricOptions ].filter(x => !embeddingMetrics.includes(x.value)));
       } else {
           getEmbedCols(props.dataset.dataset.table_name).then(cols => {
               if (cols.length > 0) {
                   setEmbedCols(cols);
               } else if (props.dataset.dataset.embed_col) {
                   setEmbedCols([ props.dataset.dataset.embed_col]);
               }
           })
       }
   }, [props.newSettings]);


   useEffect(() => {
       getTextCols(props.dataset.dataset.table_name).then(cols => {
           console.log("Retrieved text columns:", cols);
           const options = cols.map(col => ({
               value: col,
               label: col.replace(/_/g, ' ')
           }));
           setTextColOptions(options);
          
           // Auto-select ALL text columns by default if none are selected
           if (cols.length > 0 && selectedTextCols.length === 0) {
               setSelectedTextCols(options);
           }
       }).catch(error => {
           console.error("Error loading text columns:", error);
       });
   }, [props.dataset.dataset.table_name]);


   // Reset metric if switching to collocations while on an embedding metric
   useEffect(() => {
       if ((dataSource === "adj_noun" || dataSource === "subj_verb") && embeddingMetrics.includes(metric)) {
           setMetric("counts");
       }
   }, [dataSource]);

   useEffect(() => {
       // POS filtering only available for tokens, not collocations
       if (posOptionalMetrics.includes(metric) && props.dataset.dataset.preprocessing_type === "lemma" && dataSource === "tokens") {
           setPosValid(true);
       } else {
           setPosValid(false);
           setPosList([]);
       }


       if (embeddingMetrics.includes(metric) && (!embeddingMetrics.includes(lastMetric) || embedCols.length != groupOptions.length)) {
           if (checkGroupOptions) {
               setGroupList([]);
           }


           if (embedCols.length > 0) {
               // Set first embed column as selected group column
               const firstEmbedCol = [{
                   value: embedCols[0],
                   label: embedCols[0].replace(/_/g, ' ')
               }];
               setSelectedGroupCols(firstEmbedCol);
               setGroupOptions(embedCols.map(col => {
                   return {
                       value: col,
                       label: col.replace(/_/g, ' ')
                   }
               }));
           } else {
               setSelectedGroupCols([]);
               setGroupOptions([{
                   value: "",
                   label: ""
               }]);
           }
       } else if (!embeddingMetrics.includes(metric) && embeddingMetrics.includes(lastMetric)) {
           updateGroupNames();
       }


       if (metricTypes.directedGraph.includes(metric)) {
           setNetworkValid(true);
       } else {
           setNetworkValid(false);
       }


       setLastMetric(metric);
   }, [metric, embedCols, dataSource]);

   const handleUpdate = () => {
       const params = {
           table_name: props.dataset.dataset.table_name,
           text_columns: selectedTextCols.map(col => col.value),
           group_names: selectedGroupCols.map(col => col.value),
           group_list: groupList.map(x => x.value),
           metric: metric,
           word_list: searchTerms.map(x => x.value),
           pos_list: posList.map(x => x.value),
           topn: parseInt(topn),
           num_clusters: parseInt(numClusters),
           to_col: toCol,
           from_col: fromCol,
           data_source: dataSource
       };
       props.updateGraph(params);
       localStorage.setItem('graph-settings', JSON.stringify(params));
       setSavedSettings(params);
   }


   // Updates column name dropdown values
   const updateGroupNames = () => {
       getGroupNames(props.dataset.dataset.table_name).then(async (res) => {
           let _groupOptions = []
           for(let i = 0; i < res.length; i++){
               _groupOptions.push({value: res[i], label: res[i].replace(/_/g, ' ')})
           }
           setGroupOptions([..._groupOptions])
       });
   }


   const getWordSuggestions = async(params) => {
       if (params.search && selectedGroupCols.length > 0) {
           if (dataSource === "adj_noun" || dataSource === "subj_verb") {
               // Use collocations API for suggestions, filtered by pair_type
               const results = await getTopCollocations(props.dataset.dataset.table_name, {
                   search: params.search.toLowerCase(),
                   topn: 10,
                   pair_type: dataSource
               });
               // Extract pair_text values from results
               return results.map(r => r.pair_text);
           } else {
               const results = await getTopWords(props.dataset.dataset.table_name, {
                   ...params,
                   search: params.search.toLowerCase(),
                   column: selectedGroupCols[0].value,
                   values: groupList.map(x => x.value)
               });
               return results;
           }
       } else {
           return [];
       }
   }


   const getGroupSuggestions = async(params) => {
       if (selectedGroupCols.length > 0) {
           const primaryGroup = selectedGroupCols[0].value; // Use first selected group column
           return await getColumnValues(props.dataset.dataset.table_name, primaryGroup, params);
       } else {
           return [];
       }
   }


   // Updated to watch selectedGroupCols instead of group
   useEffect(() => {
       if (checkGroupOptions) {
           setSelectToggle(selectedGroupCols.length === 0);
           setRefreshGroupOptions(!refreshGroupOptions);
           if (!firstUpdate) {
               setGroupList([]);
           } else if (savedSettings) {
               setFirstUpdate(false);
           }
       }
   }, [selectedGroupCols]);


   // Validation check
   useEffect(() => {
       const settings = metricSettings[metric];
       if (selectedTextCols.length === 0) {
           setDisabled(true);
           setDisabledMessage("You must select at least one text column to analyze");
       } else if (settings.column !== false && selectedGroupCols.length === 0) {
           setDisabled(true);
           setDisabledMessage("You must select at least one column to group by for this metric");
       } else if (
           (settings.toCol && !toCol) ||
           (settings.fromCol && !fromCol)
       ) {
           setDisabled(true);
           setDisabledMessage(`You must select a to column and a from column`);
       } else if (settings.values !== false && groupList.length !== settings.values) {
           setDisabled(true);
           setDisabledMessage(`You must select ${ settings.values } column value(s) for this metric`);
       } else if (
           (settings.wordsOptional === false && settings.words !== false && searchTerms.length !== settings.words) ||
           (settings.wordsOptional === true && searchTerms.length > 0 && searchTerms.length < settings.words)
       ) {
           setDisabled(true);
           setDisabledMessage(`You must enter ${ settings.words } custom search word(s) for this metric`);
       } else {
           setDisabled(false);
           setDisabledMessage("");
       }
   }, [metric, selectedTextCols, selectedGroupCols, searchTerms, groupList, toCol, fromCol]);


   // Helper function to limit group column selection to 3
   const handleGroupColChange = (newSelectedCols) => {
       if (newSelectedCols.length <= 3) {
           setSelectedGroupCols(newSelectedCols);
           setCheckGroupOptions(true);
           // Reset group list when group columns change
           setGroupList([]);
       }
   };


   return (
       <Paper
           elevation={3}
           sx={{
               width: "100%",
               height: "100%",
               overflowY: "auto",
               padding: "16px",
               py: 8
           }}
       >
           {/* Title */}
           <Typography component="h1" variant="h3" sx={{ fontSize: '1.5rem', color: 'Black'}}>
               Visualization Settings
           </Typography>
           <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
               Data from "{ props.dataset.dataset.title }"
           </Typography>


           <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
               {/* Data Source select dropdown */}
               <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)' }}>
                   <InputLabel>Data Source</InputLabel>
                   <Select
                       value={dataSource}
                       onChange={event => {
                           setDataSource(event.target.value);
                           // Reset POS list when switching to collocations
                           if (event.target.value === "adj_noun" || event.target.value === "subj_verb") {
                               setPosList([]);
                           }
                       }}
                   >
                       <MenuItem value="tokens">Tokens (Words)</MenuItem>
                       <MenuItem
                           value="adj_noun"
                           disabled={!props.dataset.dataset.collocations_done}
                       >
                           Adjective-Noun Collocations
                           {!props.dataset.dataset.collocations_done && " (Not Available)"}
                       </MenuItem>
                       <MenuItem
                           value="subj_verb"
                           disabled={!props.dataset.dataset.collocations_done}
                       >
                           Subject-Verb Collocations
                           {!props.dataset.dataset.collocations_done && " (Not Available)"}
                       </MenuItem>
                   </Select>
               </FormControl>

               {/* Metric select dropdown */}
               <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)' }}>
                   <InputLabel>Metric</InputLabel>
                   <Select
                       value = {metric}
                       onChange = {event => setMetric(event.target.value)}
                   >
                       {
                           metricOptions
                               .filter(option => {
                                   // Hide embedding metrics when using collocations
                                   if ((dataSource === "adj_noun" || dataSource === "subj_verb") && embeddingMetrics.includes(option.value)) {
                                       return false;
                                   }
                                   return true;
                               })
                               .map(option => (
                                   <MenuItem key={option.value} value = { option.value }>
                                       { option.label }
                                   </MenuItem>
                               ))
                       }
                   </Select>
               </FormControl>


               {/* Choosing which text mined columns to use */}
                   <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)', zIndex: 50 }}>
                       <FormattedMultiSelectField
                           label="Text Columns*"
                           selectedOptions={selectedTextCols}
                           setSelectedOptions={setSelectedTextCols}
                           getData={textColOptions}
                           id="textColSelect"
                           closeMenuOnSelect={false}
                       />
                   </FormControl>


               {
                   posValid === true &&
                   <>
                       {/* Column value multiselect dropdown */}
                       <FormattedMultiSelectField
                           label = "Parts of Speech"
                           selectedOptions={posList}
                           setSelectedOptions={setPosList}
                           getData={posOptions}
                           id="posSelect"
                           closeMenuOnSelect={false}
                       />
                   </>
               }


               {
                   networkValid === false &&
                   <>
                        <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)', zIndex: 50 }}>
                            <FormattedMultiSelectField
                                label="Group By*"
                                selectedOptions={selectedGroupCols}
                                setSelectedOptions={handleGroupColChange}
                                getData={groupOptions}
                                id="groupBySelect"
                                closeMenuOnSelect={false}
                            />
                        </FormControl>


                       <FormattedMultiSelectField
                           label = "Filter For"
                           selectedOptions={groupList}
                           setSelectedOptions={setGroupList}
                           getData={params => getGroupSuggestions(params)}
                           id="valueSelect"
                           isDisabled={selectToggle}
                           closeMenuOnSelect={false}
                           refresh={refreshGroupOptions}
                       />


                       {/* Custom search + terms list */}
                       <FormattedMultiSelectField
                           label = "Custom Search"
                           selectedOptions={searchTerms}
                           setSelectedOptions={setSearchTerms}
                           getData={getWordSuggestions}
                           id="customSearchSelect"
                           closeMenuOnSelect={false}
                       />
                   </>
               }


               {
                   networkValid === true &&
                   <>
                       <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)' }}>
                           <InputLabel>To Column</InputLabel>
                           <Select
                               value = {toCol}
                               onChange = {event => {
                                   setToCol(event.target.value);
                               }}
                           >
                               <MenuItem value = ""> &nbsp;</MenuItem>
                               {
                                   groupOptions.map(option => (
                                       <MenuItem key={option.value} value = { option.value }>
                                           { option.label }
                                       </MenuItem>
                                   ))
                               }
                           </Select>
                       </FormControl>


                       <FormControl fullWidth variant="filled" sx={{ background: 'rgb(255, 255, 255)' }}>
                           <InputLabel>From Column</InputLabel>
                           <Select
                               value = {fromCol}
                               onChange = {event => {
                                   setFromCol(event.target.value);
                               }}
                           >
                               {
                                   embeddingMetrics.includes(metric) === false &&
                                   <MenuItem value = ""> &nbsp;</MenuItem>
                               }
                               {
                                   groupOptions.map(option => (
                                       <MenuItem key={option.value} value = { option.value }>
                                           { option.label }
                                       </MenuItem>
                                   ))
                               }
                           </Select>
                       </FormControl>
                   </>
               }


               {
                   (
                       (metricTypes.bar.includes(metric) && metric !== "embeddings-different" && searchTerms.length === 0) ||
                       (metricTypes.dotplot.includes(metric)) ||
                       (metricTypes.multibar.includes(metric) && searchTerms.length === 0)
                   ) && (
                       <FormattedTextField
                           id="topn"
                           label="Top Words"
                           fullWidth
                           defaultValue={topn}
                           setValue={setTopn}
                           numeric
                           sx={{ zIndex: 0 }}
                       />
                   )
               }


               {
                   clusteringMetrics.includes(metric) && (
                       <FormattedTextField
                           id="num-clusters"
                           label="Number of Clusters"
                           fullWidth
                           defaultValue={numClusters}
                           setValue={setNumClusters}
                           numeric
                           sx={{ zIndex: 0 }}
                       />
                   )
               }


               {/* Update graph button */}
               {
                   disabled === true &&
                   <Tooltip
                       arrow
                       title={disabledMessage}
                   >
                       <div>
                           <Button
                               variant="contained"
                               sx={{
                                   backgroundColor: "black",
                                   color: "white",
                                   width: "100%",
                                   mt: 2
                               }}
                               disabled={true}
                           >
                               {props.generated ? 'Update Graph' : 'Create Graph'}
                           </Button>
                       </div>
                   </Tooltip>
                  
               }
               {
                   disabled === false &&
                   <Button
                       variant="contained"
                       onClick={handleUpdate}
                       sx={{
                           backgroundColor: "black",
                           color: "white",
                           width: "100%",
                           mt: 2
                       }}
                       disabled={false}
                   >
                       {props.generated ? 'Update Graph' : 'Create Graph'}
                   </Button>
               }
           </Box>
       </Paper>
   );
}